import type { PortalAssetMediaType } from '../../types/portalAsset.js';

// What a file IS, from its first bytes (spec §5.2 step 1). PURE.
//
// ⚠️ THE FILENAME AND THE BROWSER'S TYPE ARE CLAIMS; THIS IS THE DECISION. A
// `.jpg` that is an HTML page, a "photo" that is a PDF, a HEIC renamed to .mov
// — all are decided here, before any decoder sees them. An unrecognised file is
// `null`, and the worker fails the asset rather than handing it to sharp or
// ffmpeg to see what happens.
//
// ISO-BMFF (HEIC / MP4 / MOV) is told apart by the `ftyp` box. ⚠️ `mif1` is the
// generic image-file brand shared by HEIC and AVIF, so it decides nothing on
// its own — AVIF is not on the allowlist and is refused.
//
// ⚠️⚠️ THE MAJOR BRAND DECIDES FIRST, AND ONLY THEN THE COMPATIBLE LIST
// (2026-10-08). The major brand is the file's declaration of what it IS; the
// compatible list is "also readable as". This used to scan HEIC brands across
// major AND compatible before it looked at QuickTime, so a genuine .mov whose
// compatible list carried a HEIC-family brand would have been read as a
// PHOTO — and failed as "a photo, uploaded as a video" (or worse, handed to
// heif-convert). Now a `qt  ` / MP4 major is a video whatever else it lists,
// and the compatible list only decides when the major brand is generic
// (`mif1` with `heic` compatible is still a HEIC).

export type DetectedType = { contentType: string; mediaType: PortalAssetMediaType };

/** How many leading bytes the worker reads to decide (spec: 64 KiB). */
export const DETECT_HEAD_BYTES = 64 * 1024;

const HEIC_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'hevm', 'hevs']);
const AVIF_BRANDS = new Set(['avif', 'avis']);
const QUICKTIME_BRANDS = new Set(['qt  ']);
const MP4_BRANDS = new Set(['isom', 'iso2', 'iso4', 'iso5', 'iso6', 'mp41', 'mp42', 'avc1', 'mp4v', 'M4V ', 'MSNV', 'dash']);

function ascii(b: Uint8Array, start: number, len: number): string {
  if (b.length < start + len) return '';
  return String.fromCharCode(...b.subarray(start, start + len));
}

function startsWith(b: Uint8Array, sig: readonly number[]): boolean {
  return b.length >= sig.length && sig.every((v, i) => b[i] === v);
}

/** The brands of a leading `ftyp` box, or null when there is none. */
export function ftypBrands(b: Uint8Array): { major: string; compatible: string[] } | null {
  if (ascii(b, 4, 4) !== 'ftyp') return null;
  const size = ((b[0]! << 24) | (b[1]! << 16) | (b[2]! << 8) | b[3]!) >>> 0;
  if (size < 16) return null;
  const end = Math.min(size, b.length);
  const compatible: string[] = [];
  for (let i = 16; i + 4 <= end; i += 4) compatible.push(ascii(b, i, 4));
  return { major: ascii(b, 8, 4), compatible };
}

export function detectAssetType(b: Uint8Array): DetectedType | null {
  if (startsWith(b, [0xff, 0xd8, 0xff])) return { contentType: 'image/jpeg', mediaType: 'image' };
  if (startsWith(b, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return { contentType: 'image/png', mediaType: 'image' };
  if (ascii(b, 0, 4) === 'RIFF' && ascii(b, 8, 4) === 'WEBP') return { contentType: 'image/webp', mediaType: 'image' };

  const ftyp = ftypBrands(b);
  if (!ftyp) return null;
  const { major, compatible } = ftyp;
  const HEIC: DetectedType = { contentType: 'image/heic', mediaType: 'image' };
  const MOV: DetectedType = { contentType: 'video/quicktime', mediaType: 'video' };
  const MP4: DetectedType = { contentType: 'video/mp4', mediaType: 'video' };
  // 1. the major brand
  if (AVIF_BRANDS.has(major)) return null; // an AVIF that also lists mif1 / heic is still AVIF
  if (QUICKTIME_BRANDS.has(major)) return MOV;
  if (HEIC_BRANDS.has(major)) return HEIC;
  if (MP4_BRANDS.has(major)) return MP4;
  // 2. a generic major (mif1, msf1, ...): the compatible brands decide
  if (compatible.some((x) => AVIF_BRANDS.has(x)) && !compatible.some((x) => HEIC_BRANDS.has(x))) return null;
  if (compatible.some((x) => HEIC_BRANDS.has(x))) return HEIC;
  if (compatible.some((x) => QUICKTIME_BRANDS.has(x))) return MOV;
  if (compatible.some((x) => MP4_BRANDS.has(x))) return MP4;
  return null;
}
